import { type ReactNode, useCallback } from 'react'
import toast from 'react-hot-toast'

import {
  FvLinkButton,
  useLiveEventsProcessor,
  useNavState,
} from '@fv/client-components'
import { useQueryClient } from '@fv/client-core'
import type { DocCenterLiveEvent, LiveEvent } from '@fv/models'
import type { DTO } from '@fv/models/core'

import { carrierNetworkKeys } from '@/features/carrier-network/queries'
import { useMessagesLiveEventsHandler } from '@/features/messages/hooks'
import { useNotificationsLiveEventsHandler } from '@/features/notifications/hooks'
import { quoteRequestKeys } from '@/features/quote-request/useQuoteRequest'
import { routeGuideKeys } from '@/features/route-guides/queries'
import { useDocCenterLiveEventsHandler } from '@/features/settings/doc-center/hooks'
import { useCalendarLiveEventsHandler } from '@/features/shipment-calendar/api'
import { inProcessShipmentKeys } from '@/features/shipment-list/hooks/useInProcessShipments'
import { useShipmentUploadLiveEventsHandler } from '@/features/shipments-upload/hooks'
import { systemNotificationQueryKeys } from '@/features/system-notifications/queries'
import { trackingKeys } from '@/features/track/queries'
import { quoteKeys } from '@/hooks/shipments/useQuotes'

const timeout = 1500
type InvalidationTimeouts = {
  [key: string]: ReturnType<typeof setTimeout>
}

const addRemoveTimeoutKey = 'shipment-add-remove'
const invalidationTimeouts: InvalidationTimeouts = {}
const shipmentsTimeoutKey = 'shipments'
const shipmentsQueryKey = [shipmentsTimeoutKey]
export const LiveEventsHandler = ({
  authenticateUri,
  liveEventsUri,
}: {
  authenticateUri?: string
  liveEventsUri?: string
}): ReactNode => {
  const queryClient = useQueryClient()
  const notifyRefresh = useNavState(s => s.notifyRefresh)
  const messagesLiveEventsHandler = useMessagesLiveEventsHandler()
  const notificationsLiveEventsHandler = useNotificationsLiveEventsHandler()
  const docCenterLiveEventsHandler = useDocCenterLiveEventsHandler()
  const calendarLiveEventsHandler = useCalendarLiveEventsHandler()
  const shipmentUploadLiveEventsHandler = useShipmentUploadLiveEventsHandler()
  const handleEvent = useCallback(
    (event: DTO<LiveEvent>) => {
      if (event.eventType === 'account:carriers:update') {
        queryClient.invalidateQueries({ queryKey: ['audit-settings'] })
        queryClient.invalidateQueries({ queryKey: carrierNetworkKeys.all })
      }

      if (event.eventType === 'account:users:notification') {
        notificationsLiveEventsHandler(event)
      }

      if (event.eventType.startsWith('doc-entry:')) {
        docCenterLiveEventsHandler(event as DTO<DocCenterLiveEvent>)
        return
      }

      if (event.eventType === 'export:done') {
        toast.success(
          <span>
            Export complete.{' '}
            <FvLinkButton
              theme="underlined"
              href={event.data.url}
              target="_blank"
            >
              Click here
            </FvLinkButton>{' '}
            to download the export. The link has also been sent to your email.
          </span>,
          {
            duration: 5000,
            style: { width: 'fit' },
          },
        )
      }

      if (event.eventType === 'document-batch:done') {
        toast.success(
          <span>
            Paperwork ready.{' '}
            <FvLinkButton
              theme="underlined"
              href={event.data.url}
              target="_blank"
            >
              Download here
            </FvLinkButton>
            . The link has also been sent to your email.
          </span>,
          {
            duration: 5000,
            style: { width: 'fit' },
          },
        )
      }

      if (event.eventType === 'message:update') {
        messagesLiveEventsHandler(event)
      }

      if (event.eventType === 'quote:remove') {
        const { loadId, quote } = event.data

        queryClient.setQueriesData<{ _id: string }[] | undefined>(
          { queryKey: quoteKeys.load(loadId, quote.loadQuoteVersion) },
          prev => prev?.filter(q => q._id !== quote._id),
        )
      }

      if (event.eventType === 'quote:add') {
        const { loadId, quote } = event.data

        queryClient.setQueriesData<{ _id: string }[] | undefined>(
          { queryKey: quoteKeys.load(loadId, quote.loadQuoteVersion) },
          prev =>
            prev?.some(p => p._id === quote._id) // if we already have the quote (if the update comes through before the add) don't insert it
              ? prev
              : [quote, ...(prev ? prev : [])],
        )
      }

      if (event.eventType === 'quote:update') {
        const { loadId, quote } = event.data

        queryClient.setQueriesData<{ _id: string }[] | undefined>(
          { queryKey: quoteKeys.load(loadId, quote.loadQuoteVersion) },
          prev => prev?.filter(q => q._id !== quote._id)?.concat(quote),
        )
      }

      if (
        event.eventType === 'shipper:shipment:add' ||
        event.eventType === 'shipper:shipment:remove'
      ) {
        clearTimeout(invalidationTimeouts[addRemoveTimeoutKey])

        invalidationTimeouts[addRemoveTimeoutKey] = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: inProcessShipmentKeys.all })
          queryClient.invalidateQueries({ queryKey: ['shipments', 'search'] })
        }, timeout)
      }

      if (event.eventType === 'shipper:shipment:update') {
        const { loadId, quoteRequestId } = event.data
        calendarLiveEventsHandler(event)

        // All shipment lists will be invalidated here
        clearTimeout(invalidationTimeouts[addRemoveTimeoutKey])
        clearTimeout(invalidationTimeouts[loadId])
        clearTimeout(invalidationTimeouts[shipmentsTimeoutKey])

        invalidationTimeouts[loadId] = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['load', loadId] })
          queryClient.invalidateQueries({
            queryKey: quoteRequestKeys.request(quoteRequestId),
          })
          queryClient.invalidateQueries({
            queryKey: routeGuideKeys.execution(loadId),
          })
        }, timeout)

        invalidationTimeouts[shipmentsTimeoutKey] = setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: inProcessShipmentKeys.allRecentTypes(),
          })
          queryClient.invalidateQueries({ queryKey: shipmentsQueryKey })
        }, timeout)
      }

      if (event.eventType === 'spot-quote-recipients:update') {
        const { loadId } = event.data
        const queryKey = ['spot-quote-recipients', loadId]
        const timeoutKey = queryKey.join(':')

        clearTimeout(invalidationTimeouts[timeoutKey])

        invalidationTimeouts[timeoutKey] = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: queryKey })
        }, timeout)
      }

      if (event.eventType === 'system:reset') {
        notifyRefresh()
      }

      if (event.eventType === 'system:notification') {
        queryClient.invalidateQueries({
          queryKey: systemNotificationQueryKeys.all,
        })
      }

      if (event.eventType === 'load:tracking:update') {
        const { loadId } = event.data
        queryClient.invalidateQueries({ queryKey: trackingKeys.load(loadId) })
        calendarLiveEventsHandler(event)
      }
      shipmentUploadLiveEventsHandler(event)
    },
    [
      queryClient,
      notifyRefresh,
      messagesLiveEventsHandler,
      notificationsLiveEventsHandler,
      docCenterLiveEventsHandler,
      calendarLiveEventsHandler,
    ],
  )

  useLiveEventsProcessor({
    authenticateUri,
    handleEvent,
    liveEventsUri,
  })

  return null
}
